import dayjs from "dayjs";
import cls from "classnames";
import { useEffect, useRef } from "react";
import { DecimalHelper } from "@/helpers";
import { TransferHelper } from "@/domains/transfer";
import {
  WalletCategory,
  WalletRead,
  MyWalletService,
  WalletStatus,
  WalletHelper,
} from "@/domains/wallet";
import { useMessage } from "@/contexts";
import { useAppDispatch, useAppSelector } from "@/stores";
import {
  operatorManageWalletSelects,
  operatorManageWalletThunks,
} from "./store";
// components
import { Button, Modal, Table } from "antd";
import { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { MoneyCollectOutlined } from "@ant-design/icons";
import { CreateModal } from "./CreateModal";
import { UpdateModal, UpdateModalRef } from "./UpdateModal";
import { TransferModal, TransferModalRef } from "./TransferModal";

export const OperatorManageWalletsPage = () => {
  const updateModalRef = useRef<UpdateModalRef>(null);

  const transferModalRef = useRef<TransferModalRef>(null);

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorManageWalletSelects.loading);

  const wallets = useAppSelector(operatorManageWalletSelects.wallets);

  const total = useAppSelector(operatorManageWalletSelects.total);

  const pageQuery = useAppSelector(operatorManageWalletSelects.pageQuery);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    void dispatch(
      operatorManageWalletThunks.list({
        page_index: pagination.current!,
        page_size: pagination.pageSize!,
      })
    );
  };

  const handleUpdateModalUpdate = () => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
  };

  const handleTransferModalTransfer = () => {
    void dispatch(operatorManageWalletThunks.list(pageQuery));
  };

  const handleCollect = (walletId: string) => {
    Modal.confirm({
      title: "確定要歸集此收款錢包嗎?",
      icon: <MoneyCollectOutlined />,
      okType: "danger",
      async onOk() {
        await MyWalletService.collect(walletId).run();
        void dispatch(operatorManageWalletThunks.list(pageQuery));
        void messageApi.success("已新增歸集單！");
      },
    });
  };

  const columns: ColumnsType<WalletRead> = [
    {
      width: 45,
      className: "v:top",
      title: "類型",
      key: "category",
      render(_, { category }) {
        return <div>{WalletHelper.getCategoryText(category)}</div>;
      },
    },
    {
      width: 100,
      className: "v:top",
      title: "歸屬",
      dataIndex: ["belong", "name"],
    },
    {
      width: 255,
      className: "v:top",
      title: "地址",
      dataIndex: ["address"],
      render(_, { address }) {
        return (
          <a target="_blank" href={TransferHelper.getAddressLink(address)}>
            {address}
          </a>
        );
      },
    },
    {
      width: 140,
      className: "v:top t:right",
      title: "餘額",
      render(_, record) {
        switch (record.currency) {
          case "USDT": {
            return (
              <>
                <div>
                  <span className="inline-block w:80 t:right">
                    {DecimalHelper.fixed(record.balance1, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">USDT</span>
                </div>
                <div>
                  <span className="inline-block w:80 t:right">
                    {DecimalHelper.fixed(record.balance2, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">TRX</span>
                </div>
              </>
            );
          }
          default: {
            return (
              <div>
                <span className="inline-block w:80 t:right">
                  {DecimalHelper.fixed(record.balance1, 2)}
                </span>
                <span className="inline-block w:35 t:right">
                  {record.currency}
                </span>
              </div>
            );
          }
        }
      },
    },
    {
      width: 75,
      className: "v:top t:right",
      title: "餘額上限",
      dataIndex: ["balance_max"],
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "本日總收",
      key: "daily_deposit",
      sorter: () => 1,
      render(_, { daily_deposit }) {
        return (
          <div className="t:right">
            {parseInt(daily_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "本周總收",
      key: "weekly_deposit",
      sorter: () => 1,
      render(_, { weekly_deposit }) {
        return (
          <div className="t:right">
            {parseInt(weekly_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "本月總收",
      key: "monthly_deposit",
      sorter: () => 1,
      render(_, { monthly_deposit }) {
        return (
          <div className="t:right">
            {parseInt(monthly_deposit).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "日收上限",
      key: "daily_deposit_max",
      sorter: () => 1,
      render(_, { daily_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(daily_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "周收上限",
      key: "weekly_deposit_max",
      sorter: () => 1,
      render(_, { weekly_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(weekly_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      width: 95,
      className: "v:top t:right",
      title: "月收上限",
      key: "monthly_deposit_max",
      sorter: () => 1,
      render(_, { monthly_deposit_max }) {
        return (
          <div className="t:right">
            {parseInt(monthly_deposit_max).toLocaleString()}
          </div>
        );
      },
    },
    {
      title: "備註",
      className: "v:top",
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "建立時間",
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 210,
      title: "狀態",
      key: "status",
      render(_, record) {
        const StatusButton = () => (
          <Button danger type="primary">
            {record.status === WalletStatus.disabled ? "啟用" : "停用"}
          </Button>
        );

        const UpdateButton = () => (
          <Button
            type="primary"
            onClick={() => updateModalRef.current?.open(record)}
          >
            設定
          </Button>
        );

        const isCollectable =
          record.category === WalletCategory.OperatorReceive;
        const CollectButton = () =>
          record.used ? (
            <Button type="primary" disabled>
              歸集
            </Button>
          ) : (
            <Button type="primary" onClick={() => handleCollect(record.id)}>
              歸集
            </Button>
          );

        const isTransferable =
          record.category === WalletCategory.OperatorCollect;
        const TransferButton = () => (
          <Button
            type="primary"
            className="bg:gold-66"
            onClick={() => transferModalRef.current?.open(record)}
          >
            轉帳
          </Button>
        );

        return (
          <>
            <div>
              <span
                className={cls({
                  "fg:primary": record.status === WalletStatus.enabled,
                  "fg:disabled": record.status === WalletStatus.disabled,
                })}
              >
                {record.status === WalletStatus.enabled ? "已啟用" : "已停用"}
              </span>
            </div>
            <div className="flex gap:8">
              <StatusButton />
              <UpdateButton />
              {isCollectable && <CollectButton />}
              {isTransferable && <TransferButton />}
            </div>
          </>
        );
      },
    },
  ];

  const getRowClassName = (record: WalletRead) => {
    if (record.status === WalletStatus.disabled) {
      return "fg:disabled";
    }
    return "";
  };

  return (
    <div className="flex flex:col gap:8">
      <div className="flex jc:end gap:8">
        <CreateModal />
      </div>

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={wallets}
        scroll={{ x: "1600px" }}
        pagination={{
          current: pageQuery.page_index,
          pageSize: pageQuery.page_size,
          total: total,
        }}
        onChange={handleTableChange}
        rowClassName={getRowClassName}
      ></Table>

      <UpdateModal
        ref={updateModalRef}
        onUpdate={() => handleUpdateModalUpdate()}
      />

      <TransferModal
        ref={transferModalRef}
        onTransfer={() => handleTransferModalTransfer()}
      />
    </div>
  );
};
